/**
 * PrivacyFooter — calm, low-contrast trust banner.
 *
 * Renders the workspace-wide privacy manifesto. Used at the bottom of
 * marketing/auth surfaces and inside Settings to anchor user trust.
 */
import { useEffect, useState } from "react";
import { Shield } from "lucide-react";
import { api } from "../lib/api";

export default function PrivacyFooter({ compact = false, testId = "privacy-footer" }) {
  const [manifesto, setManifesto] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/privacy/manifesto")
      .then((r) => alive && setManifesto(r.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!manifesto) return null;

  if (compact) {
    return (
      <p
        data-testid={`${testId}-compact`}
        className="text-[11px] tracking-[0.16em] uppercase text-[#6A6863] inline-flex items-center gap-2"
      >
        <Shield size={11} strokeWidth={1.6} className="text-[#8A9984]" />
        {manifesto.tagline}
      </p>
    );
  }

  return (
    <footer
      data-testid={testId}
      className="mt-20 mb-10 mx-auto max-w-3xl px-6"
    >
      <div className="rounded-3xl bg-white border hairline p-6 sm:p-7">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="h-10 w-10 rounded-2xl bg-[#F2EFEA] flex items-center justify-center shrink-0"
          >
            <Shield size={14} strokeWidth={1.6} className="text-[#1A1B2E]" />
          </span>
          <div className="flex-1 min-w-0">
            <p
              data-testid={`${testId}-tagline`}
              className="font-serif text-base text-[#171717] leading-snug"
            >
              {manifesto.tagline}
            </p>
            <ul className="mt-3 space-y-1 text-[12px] text-[#525252] leading-relaxed">
              {manifesto.bullets.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span aria-hidden className="text-[#8A9984]">·</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
